import { makeAutoObservable } from 'mobx'
import { truthy } from 'shared/lib'
import type { INormalizedApiError } from 'shared/api'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import {
  type IBaseSmartCallFlowData,
  type IFullSmartCallFlowData,
  type ISmartCallFlowStep,
} from 'entities/SmartCall/api/types'
import { type Inbox } from 'entities/Inbox/model/Inbox'
import type { IFlowAction } from 'widgets/Flow'
import { RingStore } from 'pages/settings/pages/inboxes/pages/id/pages/settings/calling/smart-call-flow/ui/SmartCallFlowBuilder/ui/actions/Ring'
import { getSmartActionNextIdsFromStep } from 'pages/settings/pages/inboxes/pages/id/pages/settings/calling/smart-call-flow/ui/SmartCallFlowBuilder/utils/getSmartActionNextIdsFromStep'
import { getSmartCallActionTypeFromStep } from 'pages/settings/pages/inboxes/pages/id/pages/settings/calling/smart-call-flow/ui/SmartCallFlowBuilder/utils/getSmartCallActionTypeFromStep'
import { ForwardCallStore } from 'pages/settings/pages/inboxes/pages/id/pages/settings/calling/smart-call-flow/ui/SmartCallFlowBuilder/ui/actions/ForwardCall'
import {
  AICallingAgentStore,
  CallEndsStore,
  CallQueueStore,
  IncomingCallStore,
  IVRStore,
  PlayAudioStore,
  SendSMSStore,
  SendToVoicemailStore,
} from '../ui/actions'
import { type ISmartCallFlowAction, type ISmartCallFlowActionType } from '../ui/actions/type'
import { AddButtonStore } from '../ui/actions/AddButton/store/AddButtonStore'
import { type ISmartCallActionStore } from '../ui/Action'

export class SmartCallFlowBuilderStore {
  constructor(
    public readonly entities: IEntitiesStore,
    readonly parent: { inbox: Inbox | null },
    public response?: IFullSmartCallFlowData
  ) {
    this._init(this.response)
    makeAutoObservable(this)
  }

  get params(): IBaseSmartCallFlowData | null {
    if (!this.trigger) return null
    return {
      name: this.name,
      description: '',
      definition: {
        trigger: this.trigger.step,
        steps: this.steps,
      },
    }
  }

  name = ''
  template: string | null = null

  get trigger() {
    const trigger = this.actions[0]
    if (trigger instanceof IncomingCallStore) {
      return trigger
    }
  }

  get steps(): IBaseSmartCallFlowData['definition']['steps'] {
    const addButtonIds = new Set(
      this.actions.filter((a) => a instanceof AddButtonStore).map((a) => a.actionStore.id)
    )
    const steps: IBaseSmartCallFlowData['definition']['steps'] = {}
    this.actions.slice(1).forEach((action) => {
      if (action instanceof AddButtonStore) return
      const step = action.step
      if (step.next !== undefined && addButtonIds.has(step.next) && step.id !== 'inboundCall') {
        steps[action.actionStore.id] = { ...step, next: undefined }
      } else {
        steps[action.actionStore.id] = step
      }
    })
    return steps
  }

  private _actionsMap: Map<string, ISmartCallFlowAction> = new Map()
  addAction = (action: ISmartCallFlowAction) => {
    this._actionsMap.set(action.actionStore.id, action)
  }

  get actions() {
    return Array.from(this._actionsMap.values())
  }

  getActionById = (id: string) => {
    return this._actionsMap.get(id)
  }

  deleteAction = (workFlowAction: IFlowAction, isNextDelete: boolean) => {
    const action = workFlowAction.actionStore
    if (action.id === this.selectedActionStore?.actionStore.id) {
      this.closeSelectedAction()
    }

    const parent = this.getActionById(action.parentId)?.actionStore
    const children = action.nextIds
      .map(this.getActionById)
      .map((action) => action?.actionStore)
      .filter(truthy)

    if (!parent) return
    if (isNextDelete) {
      const parentAction = this.getActionById(action.parentId)

      if (parentAction instanceof CallEndsStore) {
        parent.removeNextId(action.id)
        this._removeActionAndAllChildren(workFlowAction)
        this._appendAddButton(parent)
      } else {
        this._removeActionAndAllChildren(workFlowAction)
        this._appendCallEndsWithAddButton(parent, action.id)
      }
    } else {
      if (workFlowAction instanceof RingStore) {
        const noAnswerChild = this.getActionById(action.nextIds[1])?.actionStore
        const answeredChild = this.getActionById(action.nextIds[0])
        if (noAnswerChild) {
          parent.replaceNextId(action.id, noAnswerChild.id)
          noAnswerChild.setParentId(parent.id)
        } else {
          parent.removeNextId(action.id)
        }
        if (answeredChild) this._removeActionAndAllChildren(answeredChild)
      } else if (children.length === 1) {
        parent.replaceNextId(action.id, children[0].id)
        children[0].setParentId(parent.id)

        const parentAction = this.getActionById(action.parentId)
        const childAction = this.getActionById(children[0].id)
        if (parentAction instanceof CallEndsStore && childAction instanceof CallEndsStore) {
          const [childFirstNextId] = childAction.actionStore.nextIds
          const isChildTerminal =
            childAction.actionStore.nextIds.length === 1 &&
            this.getActionById(childFirstNextId) instanceof AddButtonStore
          if (isChildTerminal) {
            parent.removeNextId(children[0].id)
            this._removeActionAndAllChildren(childAction)
            this._appendAddButton(parent)
          }
        }
      } else {
        parent.removeNextId(action.id)
        children.forEach((child) => {
          parent.addNextId(child.id)
          child.setParentId(parent.id)
        })
      }
      this._actionsMap.delete(action.id)
    }
  }

  createAction = (
    actionType: ISmartCallFlowActionType,
    {
      parentId,
      id,
      childId,
      step,
    }: {
      parentId: string
      childId?: string
      id?: string
      step?: ISmartCallFlowStep
    }
  ) => {
    const commonProps = { parentId, id, childId }

    switch (actionType) {
      case 'incomingCall': {
        return new IncomingCallStore()
      }
      case 'callQueue': {
        const args = (step?.id === 'callQueue' && step.args) || undefined
        return new CallQueueStore(this.entities, { ...commonProps, args }, this.parent)
      }
      case 'callingFlow': {
        const args = (step?.id === 'callingFlow' && step.args) || undefined
        return new RingStore(this.entities, { ...commonProps, args }, this.parent)
      }
      case 'sendVoicemail': {
        const args = (step?.id === 'sendVoicemail' && step.args) || undefined
        return new SendToVoicemailStore(this.entities, { ...commonProps, args }, this.parent)
      }
      case 'playSound': {
        const args = (step?.id === 'playSound' && step.args) || undefined
        return new PlayAudioStore(this.entities, { ...commonProps, args }, this.parent)
      }
      case 'forwardCall': {
        const args = (step?.id === 'forwardCallMulti' && step.args) || undefined
        return new ForwardCallStore(this.entities, { ...commonProps, args }, this.parent)
      }
      case 'sendMessage': {
        const args = (step?.id === 'sendMessage' && step.args) || undefined
        return new SendSMSStore(this.entities, {
          ...commonProps,
          args,
          defaultInboxId: this.parent.inbox?.id,
        })
      }
      case 'ivrPhoneTree': {
        const args = (step?.id === 'ivrPhoneTree' && step.args) || undefined
        return new IVRStore(this.entities, { ...commonProps, args }, this)
      }
      case 'callEnds': {
        return new CallEndsStore({ ...commonProps })
      }
      case 'aiCallingAgent': {
        const args = (step?.id === 'aiCallingAgent' && step.args) || undefined
        return new AICallingAgentStore(this.entities, { ...commonProps, args })
      }
      case 'addButton': {
        return new AddButtonStore({ id, parentId })
      }

      default: {
        return null
      }
    }
  }

  addActionByType = (actionType: ISmartCallFlowActionType, source: string, target: string) => {
    const sourceAction = this.getActionById(source)?.actionStore
    const targetFlowAction = this.getActionById(target)
    const targetAction = targetFlowAction?.actionStore

    const newWorkflowAction: ISmartCallFlowAction | null = this.createAction(actionType, {
      parentId: source,
      childId: target,
    })

    if (newWorkflowAction) {
      if (newWorkflowAction instanceof IVRStore) {
        sourceAction?.replaceNextId(target, newWorkflowAction.actionStore.id)
        this.addAction(newWorkflowAction)
        this.setSelectedActionStore(newWorkflowAction)

        if (newWorkflowAction.brunches.length === 0) {
          const firstBranchRingAction = this.createAction('callingFlow', {
            parentId: newWorkflowAction.actionStore.id,
            childId: target,
          })
          if (firstBranchRingAction) {
            newWorkflowAction.actionStore.replaceNextId(
              target,
              firstBranchRingAction.actionStore.id
            )
            targetAction?.setParentId(firstBranchRingAction.actionStore.id)
            this.addAction(firstBranchRingAction)
          }
          const noSelectionSendToVoicemailAction = this.createAction('sendVoicemail', {
            parentId: newWorkflowAction.actionStore.id,
          })

          if (noSelectionSendToVoicemailAction) {
            newWorkflowAction.actionStore.addNextId(noSelectionSendToVoicemailAction.actionStore.id)
            this.addAction(noSelectionSendToVoicemailAction)

            const noSelectionCallEndAction = this.createAction('callEnds', {
              parentId: noSelectionSendToVoicemailAction.actionStore.id,
            })

            if (noSelectionCallEndAction) {
              noSelectionSendToVoicemailAction.actionStore.addNextId(
                noSelectionCallEndAction.actionStore.id
              )
              this.addAction(noSelectionCallEndAction)
              this._appendAddButton(noSelectionCallEndAction.actionStore)
            }
          }
          newWorkflowAction.brunches = [
            {
              key: 1,
              title: 'Support',
              nextId: newWorkflowAction.actionStore.nextIds[0],
            },
            {
              key: null,
              title: 'No selection',
              nextId: newWorkflowAction.actionStore.nextIds[1],
            },
          ]
        }
      } else if (newWorkflowAction instanceof RingStore) {
        newWorkflowAction.actionStore.removeNextId(target)
        sourceAction?.replaceNextId(target, newWorkflowAction.actionStore.id)
        this.addAction(newWorkflowAction)
        this.setSelectedActionStore(newWorkflowAction)
        this._appendCallEndsWithAddButton(newWorkflowAction.actionStore)

        const targetIsSendToVoicemail = this.getActionById(target) instanceof SendToVoicemailStore
        if (targetIsSendToVoicemail) {
          newWorkflowAction.actionStore.addNextId(target)
          targetAction?.setParentId(newWorkflowAction.actionStore.id)
        } else {
          const sendToVoicemailAction: ISmartCallFlowAction | null = this.createAction(
            'sendVoicemail',
            {
              parentId: newWorkflowAction.actionStore.id,
            }
          )
          if (sendToVoicemailAction) {
            newWorkflowAction.actionStore.addNextId(sendToVoicemailAction.actionStore.id)
            this.addAction(sendToVoicemailAction)
            this._appendCallEndsWithAddButton(sendToVoicemailAction.actionStore)
          }
          if (targetFlowAction) this._removeActionAndAllChildren(targetFlowAction)
        }
      } else if (
        newWorkflowAction instanceof ForwardCallStore ||
        newWorkflowAction instanceof SendSMSStore ||
        newWorkflowAction instanceof CallQueueStore ||
        newWorkflowAction instanceof SendToVoicemailStore
      ) {
        if (targetFlowAction) {
          this._removeActionAndAllChildren(targetFlowAction)
          sourceAction?.replaceNextId(target, newWorkflowAction.actionStore.id)
          this.addAction(newWorkflowAction)
          this.setSelectedActionStore(newWorkflowAction)

          const parentAction = this.getActionById(source)
          const isPostCallEndsContext =
            parentAction instanceof CallEndsStore || parentAction instanceof SendSMSStore
          if (isPostCallEndsContext) {
            this._appendAddButton(newWorkflowAction.actionStore, target)
          } else {
            this._appendCallEndsWithAddButton(newWorkflowAction.actionStore, target)
          }
        }
      } else {
        sourceAction?.replaceNextId(target, newWorkflowAction.actionStore.id)
        targetAction?.setParentId(newWorkflowAction.actionStore.id)

        this.addAction(newWorkflowAction)
        this.setSelectedActionStore(newWorkflowAction)
      }
    }
  }

  private _selectedActionStore: ISmartCallFlowAction | null = null
  get selectedActionStore() {
    return this._selectedActionStore
  }
  setSelectedActionStore = (selectedActionStore: ISmartCallFlowAction | null) => {
    if (selectedActionStore?.actionStore.id === this._selectedActionStore?.actionStore.id) return
    this._selectedActionStore = selectedActionStore
  }
  closeSelectedAction = () => {
    this._selectedActionStore = null
  }

  private _appendCallEnds = (
    parentActionStore: ISmartCallActionStore,
    replaceId?: string
  ): CallEndsStore => {
    const end = new CallEndsStore({ parentId: parentActionStore.id })
    if (replaceId !== undefined) {
      parentActionStore.replaceNextId(replaceId, end.actionStore.id)
    } else {
      parentActionStore.addNextId(end.actionStore.id)
    }
    this.addAction(end)
    return end
  }

  private _appendCallEndsWithAddButton = (
    parentActionStore: ISmartCallActionStore,
    replaceId?: string
  ) => {
    this._appendAddButton(this._appendCallEnds(parentActionStore, replaceId).actionStore)
  }

  private _appendAddButton = (parentActionStore: ISmartCallActionStore, replaceId?: string) => {
    const addButton = new AddButtonStore({ parentId: parentActionStore.id })
    if (replaceId !== undefined) {
      parentActionStore.replaceNextId(replaceId, addButton.actionStore.id)
    } else {
      parentActionStore.addNextId(addButton.actionStore.id)
    }
    this.addAction(addButton)
  }

  private _removeActionAndAllChildren = (action: IFlowAction) => {
    this._actionsMap.delete(action.actionStore.id)
    action.actionStore.nextIds.forEach((id: string) => {
      const actionNew = this._actionsMap.get(id)
      if (actionNew) {
        return this._removeActionAndAllChildren(actionNew)
      }
    })
  }

  get disabled() {
    return false
  }

  validateForPublish = (): boolean => {
    return this.actions
      .map((action) => ('validate' in action ? action.validate() : true))
      .every(Boolean)
  }

  setError = (error: INormalizedApiError) => {
    error.entries().forEach(([key, apiError]) => {
      const value = apiError.value
      const id = key.split('.')[0]

      if (id) {
        const action = this.getActionById(id)
        if (action && 'setApiError' in action) {
          action?.setApiError(key, value)
        }
      }
    })
  }

  buildDefaultWorkflow = () => {
    this._actionsMap.clear()

    const trigger = new IncomingCallStore()
    this.addAction(trigger)

    const ringAction = new RingStore(
      this.entities,
      {
        parentId: trigger.actionStore.id,
        args: {
          ring_type: 'simultaneous',
          user_ids: [],
          duration: 30,
          crm_property: null,
          crm_key: null,
          auto_10_members: true,
          next_on_answered: '',
          next_on_no_answer: '',
        },
      },
      this.parent
    )

    trigger.actionStore.addNextId(ringAction.actionStore.id)
    this.addAction(ringAction)

    this._appendCallEndsWithAddButton(ringAction.actionStore)
    this._appendCallEndsWithAddButton(ringAction.actionStore)
  }

  setName = (name: string) => {
    this.name = `${name} Call Flow`
  }

  loadFromWorkflow = (response: IFullSmartCallFlowData) => {
    this._actionsMap.clear()
    this._init(response)
  }

  private _init = (response?: IFullSmartCallFlowData): void => {
    if (response) {
      const definition = response.definition
      const trigger = new IncomingCallStore()
      this.addAction(trigger)

      const innerFirstStepId = definition.trigger.next

      const createAction = (
        newStepId: string,
        parentWorkflowAction: ISmartCallFlowAction,
        map: Record<string, ISmartCallFlowStep>
      ) => {
        const newStep = map[newStepId]
        if (!newStep) return

        const nextIds = getSmartActionNextIdsFromStep(newStep)
        const actionType = getSmartCallActionTypeFromStep(newStep)

        if (!actionType) return

        const newWorkflowAction = this.createAction(actionType, {
          id: newStepId,
          parentId: parentWorkflowAction.actionStore.id,
          step: newStep,
        })

        if (newWorkflowAction) {
          parentWorkflowAction.actionStore.addNextId(newWorkflowAction.actionStore.id)
          this.addAction(newWorkflowAction)

          nextIds.forEach((nextId) => {
            if (nextId) createAction(nextId, newWorkflowAction, map)
          })

          if (
            newWorkflowAction instanceof RingStore &&
            newWorkflowAction.actionStore.nextIds.length === 1
          ) {
            const noAnswerId = newWorkflowAction.actionStore.nextIds[0]
            newWorkflowAction.actionStore.removeNextId(noAnswerId)
            this._appendCallEnds(newWorkflowAction.actionStore)
            newWorkflowAction.actionStore.addNextId(noAnswerId)
          }
        }
      }

      createAction(innerFirstStepId, trigger, definition.steps)

      this.actions.forEach((action) => {
        if (
          (action instanceof CallEndsStore || action instanceof SendSMSStore) &&
          action.actionStore.nextIds.length === 0
        ) {
          this._appendAddButton(action.actionStore)
        }
      })
    } else {
      const trigger = new IncomingCallStore()
      this.addAction(trigger)
      this._appendCallEndsWithAddButton(trigger.actionStore)
    }
  }
}
